import type { TrailEntry } from "@anota/shared";
import { LIMITS } from "@anota/shared/limits";

/**
 * Lo que hizo la persona antes de dejar la nota: en qué hizo clic, a qué página fue y qué campos tocó (por
 * su nombre o etiqueta, nunca lo que escribió). Ayuda a reproducir el "le di aquí y luego pasó esto".
 * Lo que se hace dentro del propio widget no cuenta. Se guardan los últimos pasos.
 */

const entries: TrailEntry[] = [];
let installed = false;
const HOST_ID = "anota-root";

const squash = (text: string, max: number) => {
  const t = text.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

/** Cómo se llama lo que se tocó: `button «Comprar»`, `a#menu «Precios»`, `input «Email»`… */
export function describeTarget(el: Element): string {
  const target = el.closest("a, button, [role=button], input, select, textarea, label, summary") ?? el;
  const tag = target.tagName.toLowerCase();
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    const name = target.getAttribute("aria-label") || target.getAttribute("placeholder") || target.name || target.id;
    return `${tag}${name ? ` «${squash(name, 40)}»` : ""}`;
  }
  const text = squash((target as HTMLElement).innerText ?? target.textContent ?? "", 40) || target.getAttribute("aria-label") || "";
  return `${tag}${target.id ? `#${target.id}` : ""}${text ? ` «${text}»` : ""}`.slice(0, 200);
}

function push(kind: TrailEntry["kind"], label: string): void {
  entries.push({ kind, label: label.slice(0, 200), at: Date.now() });
  if (entries.length > LIMITS.trailEntries) entries.shift();
}

const ours = (e: Event) => e.composedPath().some((n) => n instanceof Element && n.id === HOST_ID);

export function installTrail(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  const safe = (fn: () => void) => {
    try {
      fn();
    } catch {
      // Nunca romper la página.
    }
  };
  document.addEventListener(
    "click",
    (e) =>
      safe(() => {
        if (!ours(e) && e.target instanceof Element) push("click", describeTarget(e.target));
      }),
    true
  );
  document.addEventListener(
    "focusin",
    (e) =>
      safe(() => {
        const t = e.target;
        if (!ours(e) && (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement)) push("field", describeTarget(t));
      }),
    true
  );

  // Cambios de página en webs de una sola página (pushState/replaceState) y atrás/adelante. Solo la ruta:
  // la query puede llevar datos, y quitar ?anota=… de la URL no es una página nueva.
  let path = location.pathname;
  const nav = () =>
    safe(() => {
      if (location.pathname === path) return;
      path = location.pathname;
      push("nav", path);
    });
  for (const method of ["pushState", "replaceState"] as const) {
    const original = history[method];
    history[method] = function (this: History, ...args: Parameters<History["pushState"]>) {
      const result = original.apply(this, args);
      nav();
      return result;
    };
  }
  addEventListener("popstate", nav);
  addEventListener("hashchange", () => safe(() => push("nav", `${location.pathname}${location.hash.split("?")[0]}`)));
}

export const trailEntries = (): TrailEntry[] => entries.slice();
